import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import t from '../../i18n/intl';
import styled from 'styled-components';
import { Dimensions } from '../../../components/visuals';
import { fetchCartByOrderNumber } from '../duck/actions/cart';
import { selectCartOrderResponse, selectVerificationHash } from '../duck/selectors';
import { colors } from '@atlaskit/theme';
import config from '../../../appconfig';
import { messages } from '../messages';
import { State } from 'model/State';
import { PartnerPortalLink } from '../../../util/constants';
import { fetchVerificationHash } from '../duck/actions/orders';
import SectionMessage from '@atlaskit/section-message';
import OrderLookupField from './OrderLookupField';
import { LookupFormGroup, SubmitButton, SubmitButtonWrapper } from './styledComponents';
import { isQuoteMigrationIsCompletedOrInProgress } from '../../../util/mac/helpers';

import traceUFOPress from '@atlassian/react-ufo/trace-press';
import { ORDER_LOOKUP_SUBMIT_UFO_NAME } from 'util/ufoNameConstants';

const OrdersTitle = styled.h3`
  margin-top: ${Dimensions.units(5)}px;
  margin-bottom: ${Dimensions.units(1.5)}px;
  font-weight: 500;
  font-size: ${Dimensions.units(2)}px;
`;

export const ErrorSectionWrapper = styled.div`
  margin-top: ${Dimensions.units(2)}px;
`;

const StyledDiv = styled.div`
  color: ${colors.N300};
`;

enum ErrorTypes {
  ORDER_NOT_FOUND = 'ORDER_NOT_FOUND',
  PARTNER_ORDER_NOT_FOUND = 'PARTNER_ORDER_NOT_FOUND',
  ORDER_MATCH_ERROR = 'ORDER_MATCH_ERROR',
  TOTAL_IS_NOT_NUMBER = 'TOTAL_IS_NOT_NUMBER',
  REQUIRED_FIELDS_NOT_FILLED = 'REQUIRED_FIELDS_NOT_FILLED',
  NONE = 'NONE',
}

const ErrorMap = {
  [ErrorTypes.ORDER_NOT_FOUND]: {
    message: messages.orderNotFound,
  },
  [ErrorTypes.PARTNER_ORDER_NOT_FOUND]: {
    message: messages.partnerOrderNotFound,
  },
  [ErrorTypes.ORDER_MATCH_ERROR]: {
    message: messages.orderMatchError,
  },
  [ErrorTypes.TOTAL_IS_NOT_NUMBER]: {
    message: messages.ordersLookupFieldsNotNumber,
  },
  [ErrorTypes.REQUIRED_FIELDS_NOT_FILLED]: {
    message: messages.ordersLookupFieldsNotCompleted,
  },
};

const ErrorSection = ({ errorType, additionalValues }) => {
  if (!errorType || errorType === ErrorTypes.NONE) return null;
  const error = ErrorMap[errorType];

  return (
    <SectionMessage appearance="error" title={error.title}>
      <FormattedMessage {...error.message} values={{ ...error.values, ...additionalValues }} />
    </SectionMessage>
  );
};

function isNumericString(value: string): boolean {
  const normalizedValue = value.replace(',', '.');
  const numericRegex = /^-?\d+(\.\d+)?$/;
  return numericRegex.test(normalizedValue) && !Number.isNaN(parseFloat(normalizedValue));
}

function redirectToPreselectedOrder(cartForOrder: any, verificationHash: string) {
  if (cartForOrder.status === 'PROCESSED' || cartForOrder.status === 'ORDER_PLACED') {
    window.location.href = `/purchase/my/billing/paidorders/${cartForOrder.orderId}?hash=${verificationHash}`;
  } else {
    window.location.href = `/purchase/my/billing/quotes/${cartForOrder.orderId}?hash=${verificationHash}`;
  }
}

function extractOrderNumber(value: string): number {
  return +value.replace('AT-', '');
}

const doesOrderMatchForm = (email: string, total: string, cartForOrder: any): boolean =>
  (cartForOrder?.cartOrderDetails?.billingContactEmail === email ||
    cartForOrder?.cartOrderDetails?.technicalContactEmail === email ||
    cartForOrder?.billingContact?.email === email ||
    cartForOrder?.technicalContact?.email === email) &&
  cartForOrder?.cartOrderDetails?.totalIncTax === parseFloat(total);

type Props = {
  isPartner?: boolean;
};

const ppcLink = (chunks) => (
  <a href={config.ppcUrl} target="_blank" rel="noopener noreferrer">
    {chunks}
  </a>
);

const partnerPortalLink = (chunks) => (
  <a href={PartnerPortalLink} target="_blank" rel="noopener noreferrer">
    {chunks}
  </a>
);

const faqLink = (chunks) => (
  <a href={config.wacPurchaseLicensingUrl} target="_blank" rel="noopener noreferrer">
    {chunks}
  </a>
);

const contactLink = (chunks) => (
  <a href={config.companyContact} target="_blank" rel="noopener noreferrer">
    {chunks}
  </a>
);

const OrderLookup = ({ isPartner }: Props) => {
  const dispatch = useDispatch();

  const [errorType, setErrorType] = useState<ErrorTypes>(ErrorTypes.NONE);
  const [formData, setFormData] = useState({
    orderNumber: '',
    total: '',
    email: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const verificationHash = useSelector((state) =>
    selectVerificationHash(extractOrderNumber(formData.orderNumber), state as State),
  );
  const cartOrderResponse = useSelector((state) =>
    selectCartOrderResponse(extractOrderNumber(formData.orderNumber), state as State),
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value.replaceAll(' ', ''),
    });
  };

  const handleSubmit = async () => {
    setErrorType(ErrorTypes.NONE);

    if (!formData.orderNumber || !formData.total || !formData.email) {
      setErrorType(ErrorTypes.REQUIRED_FIELDS_NOT_FILLED);
      return;
    }
    if (!isNumericString(formData.total)) {
      setErrorType(ErrorTypes.TOTAL_IS_NOT_NUMBER);
      return;
    }
    setIsLoading(true);
    traceUFOPress(ORDER_LOOKUP_SUBMIT_UFO_NAME);
    dispatch(
      fetchVerificationHash(
        extractOrderNumber(formData.orderNumber),
        +formData.total,
        formData.email,
      ),
    );
  };

  useEffect(() => {
    if (verificationHash) {
      dispatch(fetchCartByOrderNumber(extractOrderNumber(formData.orderNumber), verificationHash));
    }
  }, [verificationHash]);

  useEffect(() => {
    if (isLoading && cartOrderResponse?.isCompleted) {
      setErrorType(ErrorTypes.NONE);
      setIsLoading(false);
      const cartForOrder = cartOrderResponse?.ifSuccess((r) => r);

      if (
        !cartForOrder ||
        (cartForOrder?.status === 'DELETED' &&
          !isQuoteMigrationIsCompletedOrInProgress(
            cartForOrder?.status,
            cartForOrder?.ccpQuoteMigrationStatus,
          ))
      ) {
        if (isPartner) setErrorType(ErrorTypes.PARTNER_ORDER_NOT_FOUND);
        else setErrorType(ErrorTypes.ORDER_NOT_FOUND);
        return;
      }
      if (!doesOrderMatchForm(formData.email, formData.total, cartForOrder)) {
        setErrorType(ErrorTypes.ORDER_MATCH_ERROR);
        return;
      }

      redirectToPreselectedOrder(cartForOrder, verificationHash);
    }
  }, [cartOrderResponse]);

  return (
    <>
      <OrdersTitle> {t('mac.orders.lookup.title')} </OrdersTitle>
      <StyledDiv>
        <FormattedMessage {...messages.ordersLookupInfoText} />
      </StyledDiv>
      <ErrorSectionWrapper data-cy="lookup-error-message">
        <ErrorSection
          errorType={errorType}
          additionalValues={{
            ppcLink,
            partnerPortalLink,
            faqLink,
            contactLink,
          }}
        />
      </ErrorSectionWrapper>

      <LookupFormGroup>
        <OrderLookupField
          fieldName="orderNumber"
          fieldLabel={<FormattedMessage {...messages.lookupOrderNumberLabel} />}
          helperLabel={<FormattedMessage {...messages.lookupOrderNumberHint} />}
          value={formData.orderNumber}
          changeHandler={(e) => handleChange(e)}
          dataCy="order-lookup-orderid"
          width={Dimensions.units(40)}
        />
        <OrderLookupField
          fieldName="total"
          fieldLabel={<FormattedMessage {...messages.lookupOrderTotalLabel} />}
          helperLabel={<FormattedMessage {...messages.lookupOrderTotalHint} />}
          value={formData.total}
          changeHandler={(e) => handleChange(e)}
          dataCy="order-lookup-total"
          width={Dimensions.units(40)}
          type="number"
        />
        <OrderLookupField
          fieldName="email"
          fieldLabel={<FormattedMessage {...messages.lookupOrderEmailLabel} />}
          helperLabel={<FormattedMessage {...messages.lookupOrderEmailHint} />}
          value={formData.email}
          changeHandler={(e) => handleChange(e)}
          dataCy="order-lookup-email"
          width={Dimensions.units(40)}
          type="email"
        />

        <SubmitButtonWrapper>
          <SubmitButton
            data-cy="order-lookup-btn"
            appearance="primary"
            onClick={handleSubmit}
            type="submit"
            isLoading={isLoading}
          >
            <FormattedMessage {...messages.lookupButton} />
          </SubmitButton>
        </SubmitButtonWrapper>
      </LookupFormGroup>
    </>
  );
};

export default OrderLookup;
